import { Link, withPrefix, navigate } from "gatsby"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import Logo from "../../images/inpowered-logo-light.inline.svg"
import navigationV2 from "../../content/navigation-v2"
import IconBrandLift from "../../images/solutions/menu/IconBrandLift.svg"
import IconAppSubscriptions from "../../images/solutions/menu/IconAppSubscriptions.svg"
import IconSearchLift from "../../images/solutions/menu/IconSearchLift.svg"
import IconStoreVisits from "../../images/solutions/menu/IconStoreVisits.svg"
import IconAttention from "../../images/solutions/menu/IconAttention.svg"
import IconCustomerValue from "../../images/solutions/menu/IconCustomerValue.svg"
import IconEngagement from "../../images/solutions/menu/IconEngagement.svg"
import IconEfficientReach from "../../images/solutions/menu/IconEfficientReach.svg"
import "../../styles/solutions-menu.scss"

const isExternal = href => /^https?:\/\//.test(href)
const isHash = to =>
  typeof to === "string" && (to.startsWith("#") || to.includes("#"))
const toRootHash = to =>
  withPrefix(`/#${String(to).replace(/^#/, "").replace(/^\/#/, "")}`)
const getHashId = to => {
  const m = String(to || "").match(/#(.+)$/)
  return m ? m[1] : null
}

const solutionsMenuItems = [
  { label: "Brand Lift", icon: IconBrandLift, to: "/solutions/brand-lift" },
  { label: "Search Lift", icon: IconSearchLift, to: "/solutions/search-lift" },
  {
    label: "Store Visits",
    icon: IconStoreVisits,
    to: "/solutions/store-visits",
  },
  { label: "Attention", icon: IconAttention, to: "/solutions/attention" },
  {
    label: "Efficient Reach",
    icon: IconEfficientReach,
    to: "/solutions/efficient-reach",
  },
  {
    label: "Customer Value",
    icon: IconCustomerValue,
    to: "/solutions/customer-value",
  },
  { label: "Engagement", icon: IconEngagement, to: "/solutions/engagement" },
  {
    label: "App Subscriptions",
    icon: IconAppSubscriptions,
    to: "/solutions/app-subscriptions",
  },
]

const DesktopItem = React.memo(
  ({
    item,
    isSolutionsMenuOpen,
    onHashClick,
    onSolutionsMouseEnter,
    onSolutionsMouseLeave,
    onMenuMouseEnter,
    onMenuMouseLeave,
    onNavigate,
    onSolutionsMenuClose,
    navbarReady,
    onMenuLinkClick,
  }) => {
    const baseBtn = "button is-rounded is-link has-text-weight-medium"
    const baseTxt = "has-text-white"
    const needsHashHandler = item.kind === "hash" || item.kind === "anchorRoot"
    const isSolutions = String(item.text).toLowerCase() === "solutions"

    if (item.isButton) {
      return (
        <div className="navbar-item">
          {item.kind === "route" ? (
            <Link to={item.to} className={baseBtn}>
              {item.text}
            </Link>
          ) : (
            <a
              href={item.to}
              className={baseBtn}
              onClick={
                needsHashHandler
                  ? e => onHashClick(e, item, { isMobile: false })
                  : undefined
              }
            >
              {item.text}
            </a>
          )}
        </div>
      )
    }

    if (!isSolutions) {
      return (
        <div className="navbar-item">
          {item.kind === "route" ? (
            <Link to={item.to} className={baseTxt}>
              {item.icon ? (
                <img
                  src={item.icon}
                  alt={item.text}
                  style={{
                    height: 22,
                    width: "auto",
                    display: "inline-block",
                    verticalAlign: "middle",
                    position: "relative",
                    top: -3,
                  }}
                />
              ) : (
                item.text
              )}
            </Link>
          ) : (
            <a
              href={item.to}
              className={baseTxt}
              onClick={
                needsHashHandler
                  ? e => onHashClick(e, item, { isMobile: false })
                  : undefined
              }
            >
              {item.text}
            </a>
          )}
        </div>
      )
    }

    return (
      <div
        className="navbar-item solutions-item"
        onMouseEnter={onSolutionsMouseEnter}
        onMouseLeave={onSolutionsMouseLeave}
      >
        {item.kind === "hash" ||
        item.kind === "anchorRoot" ||
        (item.to && item.to.includes("#")) ? (
          <a
            href={item.to}
            className={baseTxt}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              if (item.to && item.to.includes("#")) {
                onHashClick(e, item, { isMobile: false, closeMenu: true })
              } else {
                onNavigate(item.to)
                onSolutionsMenuClose()
              }
            }}
          >
            {item.text}
            <span
              className={`solutions-chevron is-hidden-mobile ${
                isSolutionsMenuOpen ? "is-open" : ""
              }`}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </span>
          </a>
        ) : (
          <Link to={item.to} className={baseTxt} onClick={onSolutionsMenuClose}>
            {item.text}
            <span
              className={`solutions-chevron is-hidden-mobile ${
                isSolutionsMenuOpen ? "is-open" : ""
              }`}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </span>
          </Link>
        )}
        <div
          className={`solutions-menu is-hidden-mobile ${
            isSolutionsMenuOpen ? "is-open" : ""
          }`}
          onMouseEnter={onMenuMouseEnter}
          onMouseLeave={onMenuMouseLeave}
        >
          <div className="solutions-grid">
            {solutionsMenuItems.map(sol => (
              <Link
                key={sol.label}
                to={sol.to}
                className="solutions-menu-item"
                onMouseDown={e => {
                  if (!navbarReady) {
                    e.preventDefault()
                    e.stopPropagation()
                  }
                }}
                onClick={onMenuLinkClick}
              >
                <img src={sol.icon} alt={sol.label} />
                <span>{sol.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    )
  }
)

const MobileItem = React.memo(
  ({
    item,
    onHashClick,
    onCloseMobile,
    isSolutionsMenuOpen,
    onToggleSolutions,
  }) => {
    const btn = "button is-rounded is-link is-fullwidth mt-6"
    const txt = "has-text-white py-1 mb-3"
    const needsHashHandler = item.kind === "hash" || item.kind === "anchorRoot"
    const isSolutions = String(item.text).toLowerCase() === "solutions"

    if (isSolutions) {
      return (
        <div className="mobile-solutions-container">
          <button
            className={`${txt} mobile-solutions-toggle ${
              isSolutionsMenuOpen ? "is-open" : ""
            }`}
            onClick={onToggleSolutions}
          >
            <span>{item.text}</span>
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{ fontSize: "10px" }}
            />
          </button>
          {isSolutionsMenuOpen && (
            <div className="mobile-solutions-submenu">
              {solutionsMenuItems.map(sol => (
                <Link
                  key={sol.label}
                  to={sol.to}
                  className="mobile-solutions-item"
                  onClick={onCloseMobile}
                >
                  <img src={sol.icon} alt={sol.label} />
                  <span>{sol.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )
    }

    if (item.isButton) {
      return item.kind === "route" ? (
        <Link to={item.to} className={btn} onClick={onCloseMobile}>
          {item.text}
        </Link>
      ) : (
        <a
          href={item.to}
          className={btn}
          onClick={
            needsHashHandler
              ? e => onHashClick(e, item, { isMobile: true })
              : onCloseMobile
          }
        >
          {item.text}
        </a>
      )
    }

    return item.kind === "route" ? (
      <Link to={item.to} className={txt} onClick={onCloseMobile}>
        {item.icon ? (
          <img
            src={item.icon}
            alt={item.text}
            style={{ height: 24, width: "auto", display: "block" }}
          />
        ) : (
          item.text
        )}
      </Link>
    ) : (
      <a
        href={item.to}
        className={txt}
        onClick={
          needsHashHandler
            ? e => onHashClick(e, item, { isMobile: true })
            : onCloseMobile
        }
      >
        {item.text}
      </a>
    )
  }
)

const PMPHeader = ({
  className = "",
  navigation = navigationV2,
  showBanner = true,
  transparent = false,
}) => {
  const [isExpanded, setExpansion] = useState(false)
  const [isSolutionsMenuOpen, setSolutionsMenuOpen] = useState(false)
  const [isBannerCollapsed, setBannerCollapsed] = useState(false)
  const [bannerReady, setBannerReady] = useState(false)
  const [navbarReady, setNavbarReady] = useState(false)
  const menuCloseTimeoutRef = useRef(null)
  const lastYRef = useRef(0)
  const lastThemeColorRef = useRef(null)
  const bannerCollapsedRef = useRef(false)
  const isTransparentNow = transparent && !isExpanded
  const year = new Date().getFullYear()

  const closeMobile = useCallback(() => {
    setExpansion(false)
    setSolutionsMenuOpen(false)
  }, [])

  const closeSolutionsMenu = useCallback(() => {
    setSolutionsMenuOpen(false)
  }, [])

  const handleSolutionsMouseEnter = useCallback(() => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current)
      menuCloseTimeoutRef.current = null
    }
    setSolutionsMenuOpen(true)
  }, [])

  const handleSolutionsMouseLeave = useCallback(e => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current)
      menuCloseTimeoutRef.current = null
    }

    const relatedTarget = e.relatedTarget
    const solutionsItem = e.currentTarget

    if (relatedTarget && relatedTarget instanceof Node) {
      const solutionsMenu = solutionsItem.querySelector(".solutions-menu")
      if (
        solutionsItem.contains(relatedTarget) ||
        (solutionsMenu && solutionsMenu.contains(relatedTarget))
      ) {
        return
      }
    }

    menuCloseTimeoutRef.current = setTimeout(() => {
      setSolutionsMenuOpen(false)
    }, 150)
  }, [])

  const handleMenuMouseEnter = useCallback(() => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current)
      menuCloseTimeoutRef.current = null
    }
    setSolutionsMenuOpen(true)
  }, [])

  const handleMenuMouseLeave = useCallback(e => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current)
      menuCloseTimeoutRef.current = null
    }

    const relatedTarget = e.relatedTarget
    const menu = e.currentTarget

    if (
      relatedTarget &&
      relatedTarget instanceof Node &&
      menu.contains(relatedTarget)
    ) {
      return
    }

    const solutionsItem = menu.closest(".solutions-item")
    if (
      relatedTarget &&
      relatedTarget instanceof Node &&
      solutionsItem &&
      solutionsItem.contains(relatedTarget)
    ) {
      return
    }

    setSolutionsMenuOpen(false)
  }, [])

  const setThemeColor = useCallback(color => {
    if (typeof document === "undefined" || lastThemeColorRef.current === color)
      return
    lastThemeColorRef.current = color

    let meta = document.querySelector('meta[name="theme-color"]')
    if (!meta) {
      meta = document.createElement("meta")
      meta.setAttribute("name", "theme-color")
      document.head.appendChild(meta)
    }
    meta.setAttribute("content", color)

    const shim = document.querySelector(".statusbar-shim")
    if (shim) shim.style.backgroundColor = color
  }, [])

  const handleHashClick = useCallback(
    (
      e,
      item,
      { isMobile, closeMenu } = { isMobile: false, closeMenu: false }
    ) => {
      const id = getHashId(item?.to)
      if (!id) {
        if (item?.to && item.to.includes("#")) {
          const targetPath = item.to.split("#")[0] || "/"
          const hash = item.to.split("#")[1]
          navigate(
            targetPath === "/" || targetPath === "" ? `/#${hash}` : item.to
          )
          if (closeMenu) setSolutionsMenuOpen(false)
          if (isMobile) closeMobile()
        }
        return
      }

      e.preventDefault()
      if (closeMenu) setSolutionsMenuOpen(false)

      if (typeof document === "undefined" || typeof window === "undefined")
        return

      const isHomePage =
        window.location.pathname === "/" || window.location.pathname === ""

      const scrollToSection = () => {
        const el =
          document.getElementById(id) || document.querySelector(`#${id}`)
        if (!el) return false

        const headerHeight = isMobile ? 120 : 0
        const bannerHeight =
          showBanner && bannerReady && !isBannerCollapsed ? 60 : 0
        const totalOffset = headerHeight + bannerHeight

        const targetEl =
          id === "solutions-section"
            ? el
            : el.querySelector("h2.title") ||
              el.querySelector(".background-blue-light") ||
              el

        const rect = targetEl.getBoundingClientRect()
        window.scrollTo({
          top: window.scrollY + rect.top - totalOffset,
          behavior: "smooth",
        })

        return true
      }

      if (isHomePage) {
        if (scrollToSection()) {
          if (isMobile) closeMobile()
        }
        return
      }

      navigate(`/#${id}`)

      if (typeof window.___gatsby !== "undefined") {
        const handleRouteUpdate = () => {
          setTimeout(() => {
            let attempts = 0
            const tryScroll = () => {
              attempts++
              if (scrollToSection() || attempts >= 30) return
              setTimeout(tryScroll, 50)
            }
            tryScroll()
          }, 150)
        }
        window.addEventListener("routeUpdate", handleRouteUpdate, {
          once: true,
        })
      } else {
        setTimeout(() => {
          let attempts = 0
          const tryScroll = () => {
            attempts++
            if (scrollToSection() || attempts >= 30) return
            setTimeout(tryScroll, 50)
          }
          tryScroll()
        }, 150)
      }

      if (isMobile) closeMobile()
    },
    [navigate, showBanner, bannerReady, isBannerCollapsed, closeMobile]
  )

  const handleMenuLinkClick = useCallback(() => {
    setSolutionsMenuOpen(false)
  }, [])

  const toggleSolutionsMenu = useCallback(e => {
    e.preventDefault()
    setSolutionsMenuOpen(prev => !prev)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setNavbarReady(true), 500)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return

    const preloadImages = () => {
      new Image().src = "/images/solutions/menu/LayerDesktop.png"
      if (window.innerWidth <= 768) {
        new Image().src = "/images/solutions/menu/LayerMobile.png"
      }
    }

    const timer = setTimeout(preloadImages, 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const onResize = () => {
      if (
        typeof window !== "undefined" &&
        window.innerWidth >= 768 &&
        isExpanded
      ) {
        setExpansion(false)
      }
    }
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [isExpanded])

  useEffect(() => {
    if (typeof document === "undefined") return
    document.body.classList.toggle("has-top-banner", !!showBanner)
  }, [showBanner])

  useEffect(() => {
    if (!showBanner) {
      setBannerReady(false)
      return
    }
    setBannerReady(true)

    const timer = setTimeout(() => {
      const headerWrapper = document.querySelector(".header-wrapper.has-banner")
      if (headerWrapper) headerWrapper.classList.add("animate-ready")
    }, 0)

    return () => clearTimeout(timer)
  }, [showBanner])

  useEffect(() => {
    if (!showBanner) return

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        const y = window.scrollY
        const goingDown = y > lastYRef.current
        lastYRef.current = y

        if (goingDown && y > 0 && !bannerCollapsedRef.current) {
          bannerCollapsedRef.current = true
          setBannerCollapsed(true)
        } else if (y <= 2 && bannerCollapsedRef.current) {
          bannerCollapsedRef.current = false
          setBannerCollapsed(false)
        }
        ticking = false
      })
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [showBanner])

  useEffect(() => {
    bannerCollapsedRef.current = isBannerCollapsed
  }, [isBannerCollapsed])

  useEffect(() => {
    if (typeof document === "undefined") return
    const bannerVisible = showBanner && bannerReady && !isBannerCollapsed
    const color = bannerVisible ? "#0080FF" : "#190241"

    if (lastThemeColorRef.current !== color) {
      if (bannerVisible) {
        setThemeColor(color)
      } else {
        const timeoutId = setTimeout(() => setThemeColor(color), 250)
        return () => clearTimeout(timeoutId)
      }
    }
  }, [showBanner, bannerReady, isBannerCollapsed, setThemeColor])

  const navResolved = useMemo(
    () =>
      (navigation || []).map(it => {
        if (it?.anchorToRoot && isHash(it.to))
          return { ...it, to: toRootHash(it.to), kind: "anchorRoot" }
        if (isExternal(it.to)) return { ...it, kind: "external" }
        if (isHash(it.to)) {
          const normalizedTo =
            it.to.startsWith("/") && it.to.includes("#")
              ? it.to
              : it.to.startsWith("#")
              ? `/${it.to}`
              : it.to
          return { ...it, to: normalizedTo, kind: "hash" }
        }
        return { ...it, kind: "route" }
      }),
    [navigation]
  )

  return (
    <div
      className={[
        "header-wrapper",
        className,
        showBanner ? "has-banner" : "",
        bannerReady ? "banner-ready" : "",
        isBannerCollapsed ? "banner-collapsed" : "",
        isTransparentNow ? "is-transparent" : "is-solid",
        isExpanded ? "is-expanded" : "",
      ].join(" ")}
      data-banner-open={showBanner && bannerReady && !isBannerCollapsed}
    >
      <div className="top-overlay" aria-hidden="true" />

      {showBanner && (
        <div className="banner has-text-centered">
          <Link
            to="/press/inpowered-ai-opens-decisioningos-to-agencies"
            className="banner-link"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            <span className="banner-text">
              inPowered AI Launches DecisioningOS with PMG
            </span>
          </Link>
        </div>
      )}

      <nav
        className="navbar container py-4 px-5"
        role="navigation"
        aria-label="main navigation"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "70px",
        }}
      >
        <Link to="/" className="logo" aria-label="inPowered Home">
          <Logo style={{ display: "block", width: "100%", height: "auto" }} />
        </Link>

        <div
          className="is-hidden-mobile"
          style={{ background: "transparent", display: "flex" }}
        >
          {navResolved.map(item => (
            <DesktopItem
              key={item.to + item.text}
              item={item}
              isSolutionsMenuOpen={isSolutionsMenuOpen}
              onHashClick={handleHashClick}
              onSolutionsMouseEnter={handleSolutionsMouseEnter}
              onSolutionsMouseLeave={handleSolutionsMouseLeave}
              onMenuMouseEnter={handleMenuMouseEnter}
              onMenuMouseLeave={handleMenuMouseLeave}
              onNavigate={navigate}
              onSolutionsMenuClose={closeSolutionsMenu}
              navbarReady={navbarReady}
              onMenuLinkClick={handleMenuLinkClick}
            />
          ))}
        </div>

        <div
          className="is-flex is-hidden-tablet"
          style={{ alignItems: "center" }}
        >
          <button
            aria-label="menu"
            aria-expanded={isExpanded}
            onClick={() => setExpansion(!isExpanded)}
            className={`navbar-burger has-text-white ${
              isExpanded ? "is-active" : ""
            }`}
            style={{ background: "transparent", border: 0 }}
            type="button"
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
        </div>

        {isExpanded && (
          <div className="mobile-overlay-menu has-background-dark is-hidden-tablet">
            <nav className="mobile-overlay-menu--nav py-3 px-5">
              {navResolved.map(item => (
                <MobileItem
                  key={item.to + item.text}
                  item={item}
                  onHashClick={handleHashClick}
                  onCloseMobile={closeMobile}
                  isSolutionsMenuOpen={isSolutionsMenuOpen}
                  onToggleSolutions={toggleSolutionsMenu}
                />
              ))}
            </nav>
            <div className="mobile-overlay-menu--footer py-4">
              <div className="op-6 is-size-7 has-text-white">
                © Copyright {year} inPowered
              </div>
            </div>
          </div>
        )}
      </nav>
    </div>
  )
}

export default PMPHeader
